import React, { useState } from 'react';
import { Box, Button, Grid, IconButton } from '@mui/material';
import axios from '@/_utils/axios';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { getData } from '@/redux/slices/dataTableSlice';
import { OrderJobTask } from '@/redux/slices/slice_types';
import { selectCanWriteTasks } from '@/redux/selectors/authSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import Modal from '@/components/_common/Modal/Modal';
import SchemaForm from '../SchemaForm/SchemaForm';
import Spinner from '../Spinner';
import { useToast } from '../Toast';

interface TaskMetadataProps {
  task: OrderJobTask;
  onRefresh?: () => void;
}

type TaskActionButtonProps = {
  label: string;
  type: string;
  color: 'primary' | 'secondary';
  variant: 'contained' | 'outlined';
};

enum EnableMetadataStatuses {
  ready = 'ready',
  in_progress = 'in_progress'
}

const TaskMetadata = ({ task, onRefresh }: TaskMetadataProps) => {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const { error, success } = useToast();
  const [saving, setSaving] = useState(false);
  const canWriteTasks = useAppSelector(selectCanWriteTasks);
  const schemaRef = React.useRef<any>(null);

  const handleClose = (e: React.SyntheticEvent, reason?: string) => {
    if (reason === 'backdropClick') return;
    setOpen(false);
  };

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    setOpen(true);
  };

  // named this because it's not necessarily the same action every time
  const handleAction = (action: any) => {
    setSaving(true);
    const { endpoint } = action;
    const url = endpoint ? `${endpoint}${task.id}/` : `/core/tasks/${task.id}/${action.type}/`;
    const isValid = schemaRef.current?.validateForm();
    if (!isValid) {
      setSaving(false);
      // return error('Some fields have errors.');
    } else {
      const formData = schemaRef.current?.state?.formData;
      axios
        .post(url, { task_action_input: formData })
        .then(() => success('Updates saved successfully!'))
        .catch((err) => error(err.response || 'There was an error retrieving the task.'))
        .finally(() => {
          setSaving(false);
          setOpen(false);
          dispatch(getData()); // updates table
          if (onRefresh) {
            onRefresh(); // updates drawer
          }
        });
    }
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpen} color="teal" disabled={!Object.values(EnableMetadataStatuses).includes(task.status) || !!task?.extra_meta?.action_completed}>
        <PolarisIcon icon="form" height={24} />
      </IconButton>
      {open && (
        <Modal title={`${task.pretty_name} Form`} open onClose={handleClose} loading={false} icon={<PolarisIcon icon="form" />} key={String(open)}>
          {saving && <Spinner />}
          <Box sx={{ pr: 2, opacity: saving ? 0.5 : 1 }}>
            <SchemaForm
              showFormTitle
              formRef={schemaRef}
              // schema={testSchema}
              // formData={testFormData}
              // onChange={({ formData }) => console.log(formData)}
              formContext={{ defaultTitle: task.job?.title }}
              schema={task?.task_action?.schema_template || {}}
              formData={task?.task_action_input}
              readonly={!canWriteTasks}
            >
              {canWriteTasks && (
                <Grid container justifyContent="flex-end" columnSpacing={2} mt={3}>
                  {task?.task_action?.actions?.map((action: TaskActionButtonProps) => (
                    <Grid item key={action.label + action.type}>
                      <Button disabled={saving} color={action.color} variant={action.variant} type="submit" onClick={() => handleAction(action)}>
                        {action.label}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              )}
            </SchemaForm>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default TaskMetadata;
